import React, { useEffect, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { CartProvider } from "./lib/cartContext";
import AnalyticsInjector from "./components/common/AnalyticsInjector";

import SiteLayout from "./components/layout/SiteLayout";

// Eager: the core shopping flow. These are what most visitors hit first, and a
// lazy fallback flash mid-purchase is worse than the few KB saved.
import Home from "./pages/Home";
import Shop from "./pages/Shop";
import Category from "./pages/Category";
import ProductDetails from "./pages/ProductDetails";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";

// Lazy: secondary pages, and the whole admin panel (its own chunk — it also
// carries firebase/auth, which storefront visitors should never download).
const OrderConfirmation = React.lazy(() => import("./pages/OrderConfirmation"));
const TrackOrder = React.lazy(() => import("./pages/TrackOrder"));
const About = React.lazy(() => import("./pages/About"));
const Contact = React.lazy(() => import("./pages/Contact"));
const FAQ = React.lazy(() => import("./pages/FAQ"));
const Privacy = React.lazy(() => import("./pages/Privacy"));
const Terms = React.lazy(() => import("./pages/Terms"));
const NotFound = React.lazy(() => import("./pages/NotFound"));
const AdminApp = React.lazy(() => import("./AdminApp"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

// Deliberately minimal — importing anything heavy here would defeat the split.
function RouteFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center" role="status" aria-live="polite">
      <div className="h-8 w-8 rounded-full border-2 border-gold border-t-transparent animate-spin" />
      <span className="sr-only">Loading…</span>
    </div>
  );
}

function App() {
  return (
    <CartProvider>
      <AnalyticsInjector />
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* Storefront */}
            <Route element={<SiteLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/category/:slug" element={<Category />} />
              <Route path="/product/:slug" element={<ProductDetails />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/order-confirmation" element={<OrderConfirmation />} />
              <Route path="/track" element={<TrackOrder />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="*" element={<NotFound />} />
            </Route>

            {/* Admin — separate chunk, brings its own AuthProvider */}
            <Route path="/admin/*" element={<AdminApp />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: "hsl(160, 100%, 15%)",
            color: "hsl(45, 29%, 97%)",
            border: "1px solid hsl(45, 65%, 50%)",
          },
        }}
      />
    </CartProvider>
  );
}

export default App;
